import React from "react";
import { getBandConfigForScore } from "newui/features/MySuppliers/constants/performance-bands";

interface RatingDialFallbackProps {
	value: number | null;
	onChange: (score: number) => void;
	disabled?: boolean;
}

const RatingDialFallback: React.FC<RatingDialFallbackProps> = ({
	value,
	onChange,
	disabled = false,
}) => {
	const bandConfig = getBandConfigForScore(value);

	return (
		<div
			className="flex flex-col items-center gap-2"
			data-testid="rating-dial-fallback"
		>
			<input
				type="range"
				min={1}
				max={9}
				step={1}
				value={value ?? 1}
				onChange={(e) => onChange(parseInt(e.target.value, 10))}
				disabled={disabled}
				className="w-full cursor-pointer"
				aria-label="Performance score"
			/>
			<div className="flex w-full justify-between px-1 text-xs text-gray-400">
				<span>1</span>
				<span>5</span>
				<span>9</span>
			</div>
			{value !== null && (
				<div className="text-center">
					<span
						className={`text-sm font-semibold ${bandConfig?.color ?? "text-gray-600"}`}
					>
						{value}/9
					</span>
					{bandConfig && (
						<span
							className={`ml-2 text-xs ${bandConfig.color}`}
						>
							{bandConfig.label}
						</span>
					)}
				</div>
			)}
		</div>
	);
};

interface RatingDialErrorBoundaryProps {
	value: number | null;
	onChange: (score: number) => void;
	disabled?: boolean;
	children: React.ReactNode;
}

interface RatingDialErrorBoundaryState {
	hasError: boolean;
}

export class RatingDialErrorBoundary extends React.Component<
	RatingDialErrorBoundaryProps,
	RatingDialErrorBoundaryState
> {
	constructor(props: RatingDialErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(): RatingDialErrorBoundaryState {
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
		console.error("RatingDial render error:", error, errorInfo);
	}

	render(): React.ReactNode {
		if (this.state.hasError) {
			return (
				<RatingDialFallback
					value={this.props.value}
					onChange={this.props.onChange}
					disabled={this.props.disabled}
				/>
			);
		}

		return this.props.children;
	}
}
